import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Heart, LogOut, User, Home, Sparkles, Settings, ChevronDown, Compass, MessageCircle, HandHeart, Quote, Mail, HelpCircle, Lightbulb, Menu, X } from 'lucide-react';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [exploreOpen, setExploreOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setExploreOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setExploreOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate('/login');
  };

  const exploreGroups = [
    {
      heading: 'Start Here',
      items: [
        { to: '/feelings', label: 'Feelings Guide', Icon: Compass, tint: '#E57A44' },
      ],
    },
    {
      heading: 'Community',
      items: [
        { to: '/prayer-wall', label: 'Prayer Wall', Icon: HandHeart, tint: '#78A18E' },
        { to: '/anonymous', label: 'Share Anonymously', Icon: MessageCircle, tint: '#5B7C99' },
        { to: '/topics', label: 'Topic Ideas', Icon: Lightbulb, tint: '#E8C265' },
      ],
    },
    {
      heading: 'About',
      items: [
        { to: '/meet-genesis', label: 'Meet Genesis', Icon: User, tint: '#E57A44' },
        { to: '/testimonials', label: 'Testimonials', Icon: Quote, tint: '#E8C265' },
        { to: '/faq', label: 'FAQ', Icon: HelpCircle, tint: '#5B7C99' },
        { to: '/contact', label: 'Contact', Icon: Mail, tint: '#78A18E' },
      ],
    },
  ];

  return (
    <header
      className="sticky top-0 z-50 md:backdrop-blur-xl"
      style={{
        backgroundColor: 'rgba(253, 251, 247, 0.98)',
        borderBottom: '1px solid rgba(44, 39, 36, 0.08)',
        WebkitBackfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0" data-testid="header-logo">
          <div className="p-2 rounded-xl transition-all duration-300 shrink-0" style={{ backgroundColor: '#E57A44' }}>
            <Heart className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#FDFBF7', strokeWidth: 2 }} />
          </div>
          <span className="text-lg sm:text-2xl font-semibold tracking-tight truncate" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Love Restored</span>
        </Link>

        {/* Desktop nav (>= md) */}
        <nav className="hidden md:flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 hover:-translate-y-0.5" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500 }} data-testid="nav-home">
            <Home className="w-4 h-4" style={{ strokeWidth: 2 }} />
            <span>Home</span>
          </Link>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setExploreOpen(v => !v)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 hover:-translate-y-0.5"
              style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500 }}
              data-testid="nav-explore"
              aria-haspopup="menu"
              aria-expanded={exploreOpen}
            >
              <Compass className="w-4 h-4" style={{ strokeWidth: 2 }} />
              <span>Explore</span>
              <ChevronDown className="w-4 h-4 transition-transform" style={{ strokeWidth: 2, transform: exploreOpen ? 'rotate(180deg)' : 'none' }} />
            </button>
            {exploreOpen && (
              <div
                className="absolute right-0 mt-2 w-72 rounded-2xl overflow-hidden py-2"
                style={{ backgroundColor: '#FFFFFF', boxShadow: '0 12px 40px rgba(44, 39, 36, 0.12)', border: '1px solid rgba(44, 39, 36, 0.06)' }}
                role="menu"
                data-testid="nav-explore-menu"
              >
                {exploreGroups.map((group, gi) => (
                  <div key={group.heading} className={gi > 0 ? 'mt-1 pt-2' : ''} style={gi > 0 ? { borderTop: '1px solid rgba(44, 39, 36, 0.06)' } : {}}>
                    <p className="px-4 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D' }}>
                      {group.heading}
                    </p>
                    {group.items.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setExploreOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 transition-all duration-200 hover:bg-[rgba(44,39,36,0.03)]"
                        style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}
                        data-testid={`nav-explore-${item.to.slice(1)}`}
                      >
                        <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${item.tint}20` }}>
                          <item.Icon className="w-4 h-4" style={{ color: item.tint, strokeWidth: 2 }} />
                        </div>
                        <span style={{ fontWeight: 500 }}>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link to="/sanctuary" className="flex items-center gap-2 px-5 py-2 rounded-full transition-all duration-300 hover:-translate-y-0.5 ml-1" style={{ fontFamily: 'Manrope, sans-serif', background: 'linear-gradient(135deg, #E8C265 0%, #E57A44 100%)', color: '#FFFFFF', fontWeight: 700 }} data-testid="nav-sanctuary">
            <Sparkles className="w-4 h-4" style={{ strokeWidth: 2 }} />
            <span>The Sanctuary</span>
          </Link>

          <Link to="/tools" className="flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 hover:-translate-y-0.5 ml-1" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 600 }} data-testid="nav-tools">
            <span>Sacred Tools</span>
          </Link>

          {user?.role === 'admin' && (
          <Link to="/consultations" className="flex items-center gap-2 px-5 py-2 rounded-full transition-all duration-300 hover:-translate-y-0.5 ml-1" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E8C26550', color: '#2C2A29', fontWeight: 600 }} data-testid="nav-consultations">
            <Sparkles className="w-4 h-4" style={{ strokeWidth: 2 }} />
            <span>Consultations</span>
          </Link>
          )}

          {user ? (
            <div className="flex items-center gap-2 ml-2 pl-2" style={{ borderLeft: '1px solid rgba(44, 39, 36, 0.1)' }}>
              {user.role === 'admin' && (
                <Link to="/admin" className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-300" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#5C715E', color: '#FDFBF7', fontWeight: 500 }} data-testid="nav-admin">
                  <Settings className="w-4 h-4" style={{ strokeWidth: 2 }} />
                  <span>Admin</span>
                </Link>
              )}
              <Link to="/profile" className="p-2 rounded-xl transition-all duration-300 hover:bg-[rgba(44,39,36,0.05)]" style={{ color: '#2C2724' }} data-testid="nav-profile" aria-label="Profile">
                <User className="w-5 h-5" style={{ strokeWidth: 2 }} />
              </Link>
              <button onClick={handleLogout} className="p-2 rounded-xl transition-all duration-300 hover:bg-[rgba(229,122,68,0.1)]" style={{ color: '#E57A44' }} data-testid="nav-logout" aria-label="Logout">
                <LogOut className="w-5 h-5" style={{ strokeWidth: 2 }} />
              </button>
            </div>
          ) : (
            <Link to="/login" className="px-5 py-2 rounded-xl transition-all duration-300 hover:-translate-y-0.5 ml-2" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 500 }} data-testid="nav-login">
              Login
            </Link>
          )}
        </nav>

        {/* Mobile hamburger (< md) */}
        <button
          onClick={() => setMobileOpen(v => !v)}
          className="md:hidden p-2 rounded-xl transition-colors shrink-0"
          style={{ color: '#2C2724', backgroundColor: mobileOpen ? 'rgba(44,39,36,0.06)' : 'transparent' }}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" style={{ strokeWidth: 2 }} /> : <Menu className="w-6 h-6" style={{ strokeWidth: 2 }} />}
        </button>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div
          className="md:hidden border-t"
          style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(44, 39, 36, 0.08)' }}
          data-testid="mobile-menu-panel"
        >
          <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <MobileLink to="/" Icon={Home} label="Home" testid="mobile-nav-home" tint="#2C2724" />

            <div className="pt-2 pb-1 px-3 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8C867D' }}>Start Here</div>
            {exploreGroups[0].items.map((it) => (
              <MobileLink key={it.to} to={it.to} Icon={it.Icon} label={it.label} tint={it.tint} testid={`mobile-nav-${it.to.slice(1)}`} />
            ))}

            <div className="pt-2 pb-1 px-3 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8C867D' }}>Community</div>
            {exploreGroups[1].items.map((it) => (
              <MobileLink key={it.to} to={it.to} Icon={it.Icon} label={it.label} tint={it.tint} testid={`mobile-nav-${it.to.slice(1)}`} />
            ))}

            <div className="pt-2 pb-1 px-3 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8C867D' }}>About</div>
            {exploreGroups[2].items.map((it) => (
              <MobileLink key={it.to} to={it.to} Icon={it.Icon} label={it.label} tint={it.tint} testid={`mobile-nav-${it.to.slice(1)}`} />
            ))}

            <div className="pt-3 mt-2" style={{ borderTop: '1px solid rgba(44, 39, 36, 0.08)' }} />

            <Link
              to="/sanctuary"
              className="flex items-center gap-3 px-4 py-3 rounded-xl mt-1"
              style={{ background: 'linear-gradient(135deg, #E8C265 0%, #E57A44 100%)', color: '#FFFFFF', fontWeight: 700 }}
              data-testid="mobile-nav-sanctuary"
            >
              <Sparkles className="w-5 h-5" style={{ strokeWidth: 2 }} />
              <span>The Sanctuary</span>
            </Link>

            <Link
              to="/tools"
              className="flex items-center gap-3 px-4 py-3 rounded-xl mt-1"
              style={{ backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 600 }}
              data-testid="mobile-nav-tools"
            >
              <Sparkles className="w-5 h-5" style={{ strokeWidth: 2 }} />
              <span>Sacred Tools</span>
            </Link>

            {user?.role === 'admin' && (
              <Link
                to="/consultations"
                className="flex items-center gap-3 px-4 py-3 rounded-xl mt-1"
                style={{ backgroundColor: '#E8C26550', color: '#2C2A29', fontWeight: 600 }}
                data-testid="mobile-nav-consultations"
              >
                <Sparkles className="w-5 h-5" style={{ strokeWidth: 2 }} />
                <span>Consultations</span>
              </Link>
            )}

            <div className="pt-3 mt-2" style={{ borderTop: '1px solid rgba(44, 39, 36, 0.08)' }} />

            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl"
                    style={{ backgroundColor: '#5C715E', color: '#FDFBF7', fontWeight: 500 }}
                    data-testid="mobile-nav-admin"
                  >
                    <Settings className="w-5 h-5" style={{ strokeWidth: 2 }} />
                    <span>Admin</span>
                  </Link>
                )}
                <MobileLink to="/profile" Icon={User} label="Profile" testid="mobile-nav-profile" tint="#2C2724" />
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors hover:bg-[rgba(229,122,68,0.08)]"
                  style={{ color: '#E57A44', fontWeight: 500 }}
                  data-testid="mobile-nav-logout"
                >
                  <LogOut className="w-5 h-5" style={{ strokeWidth: 2 }} />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl mt-1"
                style={{ backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
                data-testid="mobile-nav-login"
              >
                Login
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, Icon, label, tint = '#2C2724', testid }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors hover:bg-[rgba(44,39,36,0.04)]"
      style={{ color: '#2C2724', fontWeight: 500 }}
      data-testid={testid}
    >
      <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${tint}20` }}>
        <Icon className="w-4 h-4" style={{ color: tint, strokeWidth: 2 }} />
      </div>
      <span>{label}</span>
    </Link>
  );
}
